<!-- eslint-disable max-len -->
<template>
  <HomeHeader />

  <main
    v-if="dataLoaded"
    ref="pageRoot"
    class="isolate relative"
  >
    <!-- ============ HERO ============ -->
    <div class="ll-hero relative overflow-hidden">
      <div
        class="ll-hero-bg absolute inset-0 -z-20"
        aria-hidden="true"
      />
      <div
        class="ll-hero-grid absolute inset-0 -z-10"
        aria-hidden="true"
      />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 pt-16 pb-16 lg:pt-20">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div>
            <span class="shadow inline-flex items-center rounded-full bg-white px-4 py-2 mb-6 text-base font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset">
              <SparklesIcon
                class="size-6 mr-2 fill-teal-500"
                aria-hidden="true"
              />
              {{ t('home.hero.tag') }}
            </span>
            <h1 class="text-5xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-6xl">
              {{ t('home.landing.hero.title1') }}
              <em class="ll-hl not-italic text-teal-600 whitespace-nowrap relative">
                {{ t('home.landing.hero.titleHighlight') }}
                <svg
                  viewBox="0 0 200 10"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                ><path d="M2 8 Q 50 2 100 6 T 198 4" /></svg>
              </em>
              {{ t('home.landing.hero.title2') }}
            </h1>
            <p class="mt-8 text-lg font-medium text-pretty text-gray-500 sm:text-xl/8 max-w-xl">
              {{ t('home.landing.hero.description') }}
            </p>
            <div class="mt-8 flex flex-wrap items-center gap-4">
              <a
                :href="signupLink()"
                class="inline-block rounded-xl border border-transparent bg-teal-600 px-7 py-3 shadow-xl text-center font-semibold text-white hover:bg-teal-700 transition-all duration-200 focus-visible:outline-teal-600"
              >
                {{ t('home.hero.cta') }}
              </a>
              <a
                href="#como-funciona"
                class="inline-block rounded-xl border border-gray-200 bg-white px-7 py-3 shadow-sm text-center font-semibold text-gray-700 hover:border-gray-300 hover:text-gray-900 transition-all duration-200"
              >
                {{ t('home.landing.hero.ctaSecondary') }}
              </a>
            </div>
            <div class="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 font-medium">
              <span class="inline-flex items-center gap-1.5 font-semibold text-gray-700">
                <span class="ll-meta-dot" />
                {{ t('home.landing.hero.metaBadge') }}
              </span>
              <span aria-hidden="true">·</span>
              <span>{{ t('home.landing.hero.metaNote') }}</span>
            </div>
          </div>

          <!-- Animated mockup: live phone + report card + DM chip -->
          <div
            class="ll-hero-visual"
            aria-hidden="true"
          >
            <div class="ll-phone">
              <div class="ll-phone-screen" />
              <div class="ll-live-topbar">
                <span class="ll-live-avatar" />
                <span class="ll-live-handle">@sua.loja</span>
                <span class="ll-live-pill"><span class="ll-dot-w" />{{ t('home.landing.mock.live') }}</span>
                <span class="ll-viewers">👁 1.284</span>
              </div>
              <div class="ll-comments">
                <div
                  v-for="c in liveComments"
                  :key="c.id"
                  class="ll-comment"
                >
                  <span
                    class="ll-c-av"
                    :style="{ background: c.color }"
                  />
                  <span class="ll-c-body"><b>{{ c.name }}</b>{{ c.text }}</span>
                </div>
              </div>
              <div class="ll-hearts">
                <span class="ll-heart">❤️</span><span class="ll-heart">🧡</span><span class="ll-heart">💜</span><span class="ll-heart">❤️</span>
              </div>
            </div>

            <div class="ll-report-card">
              <div class="ll-report-head">
                <span class="ll-vis-title">{{ t('home.landing.mock.report.title') }}</span>
                <span class="ll-pill-teal"><span class="ll-dot-pulse" />{{ t('home.landing.mock.report.realtime') }}</span>
              </div>
              <div class="ll-report-stats">
                <div class="ll-rstat">
                  <b>{{ mockStats.msg }}</b><span>{{ t('home.landing.mock.report.messages') }}</span>
                </div>
                <div class="ll-rstat">
                  <b>{{ mockStats.cart }}</b><span>{{ t('home.landing.mock.report.carts') }}</span>
                </div>
                <div class="ll-rstat">
                  <b>{{ mockStats.dm }}</b><span>{{ t('home.landing.mock.report.dms') }}</span>
                </div>
              </div>
              <div
                class="ll-report-row"
                style="animation-delay:.8s"
              >
                <span class="ll-r-ic ll-bg-amber">🔥</span>
                <span class="ll-r-tx"><b>{{ t('home.landing.mock.report.hotTitle') }}</b> · {{ t('home.landing.mock.report.hotDesc') }}</span>
              </div>
              <div
                class="ll-report-row"
                style="animation-delay:1.6s"
              >
                <span class="ll-r-ic ll-bg-teal">🛒</span>
                <span class="ll-r-tx"><b>{{ t('home.landing.mock.report.cartTitle') }}</b> · {{ t('home.landing.mock.report.cartDesc') }}</span>
                <span class="ll-r-badge ll-bg-teal">{{ t('home.landing.mock.report.newBadge') }}</span>
              </div>
              <div
                class="ll-report-row"
                style="animation-delay:2.4s"
              >
                <span class="ll-r-ic ll-bg-violet">✨</span>
                <span class="ll-r-tx"><b>{{ t('home.landing.mock.report.iaTitle') }}</b> · {{ t('home.landing.mock.report.iaDesc') }}</span>
                <span class="ll-r-badge ll-bg-violet">{{ t('home.landing.mock.report.iaBadge') }}</span>
              </div>
              <div
                class="ll-report-row"
                style="animation-delay:3.2s"
              >
                <span class="ll-r-ic ll-bg-rose">💬</span>
                <span class="ll-r-tx"><b>{{ t('home.landing.mock.report.qTitle') }}</b> {{ t('home.landing.mock.report.qDesc') }}</span>
              </div>
            </div>

            <div class="ll-dm-chip">
              <span class="ll-dm-ic">✉</span>
              <span class="ll-dm-tx"><b>{{ t('home.landing.mock.dm.title') }}</b><br>{{ t('home.landing.mock.dm.text') }}</span>
              <span class="ll-dm-check">✓✓</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ STATS ============ -->
    <div class="bg-gray-50 border-y border-gray-200">
      <div class="mx-auto max-w-7xl px-6 lg:px-8 py-20">
        <h2 class="ll-reveal text-center text-3xl font-semibold tracking-tight text-gray-900 max-w-2xl mx-auto">
          {{ t('home.stats.title') }}
        </h2>
        <dl class="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            v-for="stat in landingStats"
            :key="stat.label"
            class="ll-reveal ll-stat-card rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 p-6"
          >
            <dd class="text-4xl font-semibold tracking-tight text-teal-600 tabular-nums">
              <span
                class="ll-count"
                :data-target="stat.target"
                :data-decimals="stat.decimals"
                :data-suffix="stat.suffix"
              >0</span>
            </dd>
            <dt class="mt-2 text-sm text-gray-500 font-medium">
              {{ stat.label }}
            </dt>
          </div>
        </dl>
      </div>
    </div>

    <!-- ============ JOURNEY ============ -->
    <div
      id="como-funciona"
      class="mx-auto max-w-7xl px-6 lg:px-8 py-24 text-center"
    >
      <span class="ll-reveal ll-section-tag">{{ t('home.landing.journey.tag') }}</span>
      <h2 class="ll-reveal text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
        {{ t('home.landing.journey.title') }}
      </h2>
      <p class="ll-reveal mt-6 text-lg/8 text-gray-600 max-w-2xl mx-auto">
        {{ t('home.landing.journey.sub') }}
      </p>

      <div class="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 text-left">
        <div
          v-for="(step, i) in journeySteps"
          :key="step.title"
          class="ll-reveal ll-step-card"
          :style="{ transitionDelay: `${i * 0.08}s` }"
        >
          <span class="ll-step-num">{{ i + 1 }}</span>
          <span
            class="ll-step-ic"
            :style="{ background: step.bg, color: step.color }"
          >{{ step.emoji }}</span>
          <h3 class="text-lg font-semibold text-gray-900 mb-1.5">
            {{ step.title }}
          </h3>
          <p class="text-sm text-gray-500">
            {{ step.desc }}
          </p>
        </div>
      </div>

      <div class="ll-reveal ll-livvia-strip">
        <span class="ll-livvia-av">L</span>
        <div class="text-left">
          <b class="ll-livvia-name block text-xs font-semibold mb-1">{{ t('home.landing.journey.livviaName') }}</b>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div
            class="ll-livvia-bubble"
            v-html="t('home.landing.journey.livviaMsg')"
          />
        </div>
      </div>
    </div>

    <!-- ============ FEATURE SPOTLIGHTS ============ -->
    <div
      id="recursos"
      class="bg-gray-50 border-y border-gray-200"
    >
      <div class="mx-auto max-w-7xl px-6 lg:px-8 py-24">
        <div class="text-center">
          <span class="ll-reveal ll-section-tag">{{ t('home.landing.spots.tag') }}</span>
          <h2 class="ll-reveal text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
            {{ t('home.landing.spots.title') }}
          </h2>
          <p class="ll-reveal mt-6 text-lg/8 text-gray-600 max-w-2xl mx-auto">
            {{ t('home.landing.spots.sub') }}
          </p>
        </div>

        <!-- 1 · Report -->
        <div class="ll-spot ll-reveal ll-spot-anim">
          <div>
            <span class="ll-spot-kicker ll-kick-teal">{{ t('home.landing.spots.report.kicker') }}</span>
            <h3>{{ t('home.landing.spots.report.title') }}</h3>
            <p class="ll-desc">
              {{ t('home.landing.spots.report.desc') }}
            </p>
            <ul class="ll-checks">
              <li>{{ t('home.landing.spots.report.c1') }}</li>
              <li>{{ t('home.landing.spots.report.c2') }}</li>
              <li>{{ t('home.landing.spots.report.c3') }}</li>
            </ul>
          </div>
          <div>
            <div class="ll-vis-panel">
              <div class="ll-vis-head">
                <span class="ll-vis-title">{{ t('home.landing.spots.report.visTitle') }}</span>
                <span class="ll-pill-teal"><span class="ll-dot-pulse" />{{ t('home.landing.spots.report.pill') }}</span>
              </div>
              <div class="ll-vis-tabs">
                <span class="ll-vis-tab ll-on">{{ t('home.landing.spots.report.tab1') }}</span>
                <span class="ll-vis-tab">{{ t('home.landing.spots.report.tab2') }}</span>
                <span class="ll-vis-tab">{{ t('home.landing.spots.report.tab3') }}</span>
              </div>
              <div
                class="ll-bars"
                aria-hidden="true"
              >
                <i style="height:22%" /><i style="height:35%" /><i style="height:28%" /><i style="height:48%" />
                <i style="height:41%" /><i style="height:62%" /><i
                  class="ll-peak"
                  :data-peak="t('home.landing.spots.report.peak')"
                  style="height:100%"
                /><i style="height:74%" />
                <i style="height:52%" /><i style="height:60%" /><i style="height:38%" /><i style="height:26%" />
              </div>
              <div class="ll-msg-feed">
                <div class="ll-msg-row">
                  <span
                    class="ll-m-av"
                    style="background:linear-gradient(135deg,#fbbf24,#f97316)"
                  />
                  <span><b>carla.brecho</b> · {{ t('home.landing.spots.report.m1') }}</span>
                  <span class="ll-m-tag ll-kick-teal">{{ t('home.landing.spots.report.tagOrder') }}</span>
                </div>
                <div class="ll-msg-row">
                  <span
                    class="ll-m-av"
                    style="background:linear-gradient(135deg,#a78bfa,#6366f1)"
                  />
                  <span><b>ju_semijoias</b> · {{ t('home.landing.spots.report.m2') }}</span>
                  <span class="ll-m-tag ll-kick-amber">{{ t('home.landing.spots.report.tagDoubt') }}</span>
                </div>
                <div class="ll-msg-row">
                  <span
                    class="ll-m-av"
                    style="background:linear-gradient(135deg,#f472b6,#ec4899)"
                  />
                  <span><b>maria.moda</b> · {{ t('home.landing.spots.report.m3') }}</span>
                  <span class="ll-m-tag ll-kick-teal">{{ t('home.landing.spots.report.tagOrder') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 2 · Carts + Automations (flipped) -->
        <div class="ll-spot ll-flip ll-reveal ll-spot-anim">
          <div>
            <span class="ll-spot-kicker ll-kick-pink">{{ t('home.landing.spots.carts.kicker') }}</span>
            <h3>{{ t('home.landing.spots.carts.title') }}</h3>
            <p class="ll-desc">
              {{ t('home.landing.spots.carts.desc') }}
            </p>
            <ul class="ll-checks">
              <li>{{ t('home.landing.spots.carts.c1') }}</li>
              <li>{{ t('home.landing.spots.carts.c2') }}</li>
              <li>{{ t('home.landing.spots.carts.c3') }}</li>
            </ul>
          </div>
          <div>
            <div class="ll-vis-panel">
              <div class="ll-vis-head">
                <span class="ll-vis-title">{{ t('home.landing.spots.carts.visTitle') }}</span>
                <span class="ll-pill-pink"><span class="ll-dot-pulse" />{{ t('home.landing.spots.carts.pill') }}</span>
              </div>
              <div class="ll-flow">
                <div class="ll-f-el ll-f-comment">
                  <span class="ll-f-av" />
                  <span class="ll-f-bub"><b>{{ t('home.landing.spots.carts.commentBy') }}</b>{{ t('home.landing.spots.carts.comment') }}</span>
                </div>
                <div class="ll-f-el ll-f-cart">
                  <div class="ll-fc-head">
                    {{ t('home.landing.spots.carts.cartTitle') }}
                    <span class="ll-fc-badge">{{ t('home.landing.spots.carts.cartBadge') }}</span>
                  </div>
                  <div class="ll-f-item">
                    <span>{{ t('home.landing.spots.carts.item1') }}</span><span>R$ 199,00</span>
                  </div>
                  <div class="ll-f-item">
                    <span>{{ t('home.landing.spots.carts.item2') }}</span><span>R$ 0,00</span>
                  </div>
                  <div class="ll-f-total">
                    <span>{{ t('home.landing.spots.carts.total') }}</span><span>R$ 199,00</span>
                  </div>
                </div>
                <div class="ll-f-el ll-f-dm">
                  <span class="ll-f-dm-bub">{{ t('home.landing.spots.carts.dm') }}<span class="ll-f-ck">{{ t('home.landing.spots.carts.dmStatus') }}</span></span>
                </div>
                <div class="ll-f-el ll-f-note">
                  {{ t('home.landing.spots.carts.note') }}
                </div>
              </div>
              <div class="ll-kw-chips">
                <span class="ll-kw-chip">{{ t('home.landing.spots.carts.kw1') }}</span>
                <span class="ll-kw-chip">{{ t('home.landing.spots.carts.kw2') }}</span>
                <span class="ll-kw-chip">{{ t('home.landing.spots.carts.kw3') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 3 · Transcription + AI -->
        <div class="ll-spot ll-reveal ll-spot-anim">
          <div>
            <span class="ll-spot-kicker ll-kick-violet">{{ t('home.landing.spots.ia.kicker') }}</span>
            <h3>{{ t('home.landing.spots.ia.title') }}</h3>
            <p class="ll-desc">
              {{ t('home.landing.spots.ia.desc') }}
            </p>
            <ul class="ll-checks">
              <li>{{ t('home.landing.spots.ia.c1') }}</li>
              <li>{{ t('home.landing.spots.ia.c2') }}</li>
              <li>{{ t('home.landing.spots.ia.c3') }}</li>
            </ul>
          </div>
          <div>
            <div class="ll-vis-panel">
              <div class="ll-vis-head">
                <span class="ll-vis-title">{{ t('home.landing.spots.ia.visTitle') }}</span>
                <span class="ll-pill-violet"><span class="ll-dot-pulse" />{{ t('home.landing.spots.ia.pill') }}</span>
              </div>
              <div class="ll-player">
                <span class="ll-play-btn">▶</span>
                <span
                  class="ll-wv"
                  aria-hidden="true"
                >
                  <i
                    v-for="(h, i) in waveHeights"
                    :key="i"
                    :style="{ height: `${h}%`, animationDelay: `${(i % 7) * 0.05}s` }"
                  />
                </span>
                <span class="ll-p-time">21h54 / 23h12</span>
              </div>
              <!-- eslint-disable vue/no-v-html -->
              <div class="ll-tline ll-tl1">
                <span class="ll-ts">21h52</span><span v-html="t('home.landing.spots.ia.line1')" />
              </div>
              <div class="ll-tline ll-tl2">
                <span class="ll-ts">21h54</span><span v-html="t('home.landing.spots.ia.line2')" />
              </div>
              <div class="ll-tline ll-tl3">
                <span class="ll-ts">21h56</span><span v-html="t('home.landing.spots.ia.line3')" />
              </div>
              <!-- eslint-enable vue/no-v-html -->
              <div class="ll-ia-chips">
                <span class="ll-ia-chip ll-kick-violet">{{ t('home.landing.spots.ia.chip1') }}</span>
                <span class="ll-ia-chip ll-kick-amber">{{ t('home.landing.spots.ia.chip2') }}</span>
              </div>
              <span class="ll-prog" />
            </div>
          </div>
        </div>

        <!-- 4 · CRM (flipped) -->
        <div class="ll-spot ll-flip ll-reveal ll-spot-anim">
          <div>
            <span class="ll-spot-kicker ll-kick-blue">{{ t('home.landing.spots.crm.kicker') }}</span>
            <h3>{{ t('home.landing.spots.crm.title') }}</h3>
            <p class="ll-desc">
              {{ t('home.landing.spots.crm.desc') }}
            </p>
            <ul class="ll-checks">
              <li>{{ t('home.landing.spots.crm.c1') }}</li>
              <li>{{ t('home.landing.spots.crm.c2') }}</li>
              <li>{{ t('home.landing.spots.crm.c3') }}</li>
            </ul>
          </div>
          <div>
            <div class="ll-vis-panel">
              <div class="ll-vis-head">
                <span class="ll-vis-title">{{ t('home.landing.spots.crm.visTitle') }}</span>
                <span class="ll-pill-blue"><span class="ll-dot-pulse" />{{ t('home.landing.spots.crm.pill') }}</span>
              </div>
              <div class="ll-kpis">
                <div class="ll-kpi">
                  <b><span
                    class="ll-count"
                    data-target="1284"
                    data-decimals="0"
                    data-suffix=""
                  >0</span></b>
                  <span>{{ t('home.landing.spots.crm.kpi1') }}</span>
                </div>
                <div class="ll-kpi">
                  <b><span
                    class="ll-count"
                    data-target="38"
                    data-decimals="0"
                    data-suffix="%"
                  >0</span></b>
                  <span>{{ t('home.landing.spots.crm.kpi2') }}</span>
                </div>
              </div>
              <div
                v-for="seg in crmSegments"
                :key="seg.label"
                class="ll-seg"
              >
                <div class="ll-seg-label">
                  <span>{{ seg.label }}</span><b>{{ seg.pct }}%</b>
                </div>
                <div class="ll-seg-track">
                  <div
                    class="ll-seg-fill"
                    :style="{ '--w': `${seg.pct}%`, background: seg.bg }"
                  />
                </div>
              </div>
              <div class="ll-crm-row">
                <span class="ll-crm-av" />
                <span><b>maria.moda</b> · {{ t('home.landing.spots.crm.row') }}</span>
                <span class="ll-m-tag ll-kick-violet">{{ t('home.landing.spots.crm.rowBadge') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 5 · Bio Link -->
        <div class="ll-spot ll-reveal ll-spot-anim">
          <div>
            <span class="ll-spot-kicker ll-kick-teal">{{ t('home.landing.spots.bio.kicker') }}</span>
            <h3>{{ t('home.landing.spots.bio.title') }}</h3>
            <p class="ll-desc">
              {{ t('home.landing.spots.bio.desc') }}
            </p>
            <ul class="ll-checks">
              <li>{{ t('home.landing.spots.bio.c1') }}</li>
              <li>{{ t('home.landing.spots.bio.c2') }}</li>
              <li>{{ t('home.landing.spots.bio.c3') }}</li>
            </ul>
          </div>
          <div class="ll-bio-wrap">
            <div class="relative">
              <div class="ll-bphone">
                <div class="ll-bscreen">
                  <div class="ll-bio-accent">
                    <div class="ll-b-av">
                      B
                    </div>
                    <div class="ll-b-name">
                      {{ t('home.landing.spots.bio.name') }}
                    </div>
                    <div class="ll-b-handle">
                      {{ t('home.landing.spots.bio.handle') }}
                    </div>
                    <span class="ll-b-link">{{ t('home.landing.spots.bio.l1') }}</span>
                    <span class="ll-b-link ll-ghost">{{ t('home.landing.spots.bio.l2') }}</span>
                    <span class="ll-b-link ll-ghost">{{ t('home.landing.spots.bio.l3') }}</span>
                    <div class="ll-b-products">
                      <div class="ll-b-prod">
                        <div class="ll-b-ph" /><b>{{ t('home.landing.spots.bio.p1') }}</b><span>R$ 199</span>
                      </div>
                      <div class="ll-b-prod">
                        <div class="ll-b-ph" /><b>{{ t('home.landing.spots.bio.p2') }}</b><span>R$ 119</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="ll-bio-side"
                aria-hidden="true"
              >
                <span
                  class="ll-theme-dot"
                  style="background:#0d9488"
                />
                <span
                  class="ll-theme-dot"
                  style="background:#ec4899"
                />
                <span
                  class="ll-theme-dot"
                  style="background:#8b5cf6"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ TRUSTED BY / CUSTOMERS ============ -->
    <div
      id="made-for-you"
      class="mx-auto pt-24 max-w-7xl px-6 lg:px-8"
    >
      <div class="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2">
        <div class="mx-auto w-full max-w-xl lg:mx-0">
          <span class="shadow inline-flex items-center rounded-full bg-white px-4 py-2 mb-8 text-base font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset">
            <CheckBadgeIcon
              class="size-6 mr-2 fill-teal-500"
              aria-hidden="true"
            />
            {{ t('home.trusted.tag') }}
          </span>
          <h2 class="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
            {{ t('home.trusted.title') }}
          </h2>
          <p class="mt-6 text-lg/8 text-gray-600">
            {{ t('home.trusted.description') }}
          </p>
          <div class="mt-8 flex items-center gap-x-6">
            <a
              :href="signupLink()"
              class="rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-xl hover:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              {{ t('home.trusted.cta') }}
            </a>
            <a
              href="#"
              class="text-sm font-semibold text-gray-900"
              @click.prevent="navigateWhatsapp(t('home.reviews.message'))"
            >
              {{ t('home.trusted.contact') }} <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
        </div>
        <ul class="mx-auto grid w-full max-w-xl grid-cols-5 items-center gap-y-4 gap-x-4 sm:gap-y-8 lg:mx-0 lg:max-w-none lg:pl-8">
          <li
            v-for="customer in customers"
            :key="customer.name"
          >
            <a
              :href="customer.href"
              target="_blank"
            >
              <span class="sr-only">{{ customer.name }}</span>
              <img
                class="grayscale hover:grayscale-0 aspect-14/13 w-full rounded-2xl object-cover max-w-none shadow-xl"
                :src="customer.image"
                :alt="customer.name"
              >
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- ============ REVIEWS ============ -->
    <div class="relative mx-auto -z-10 mt-24 max-w-7xl px-6 lg:px-8">
      <div
        class="absolute inset-x-0 top-1/2 -z-10 flex -translate-y-1/2 transform-gpu justify-center overflow-hidden blur-[120px] sm:top-auto sm:right-[calc(50%-6rem)] sm:bottom-0 sm:translate-y-0 sm:justify-end"
        aria-hidden="true"
      >
        <div
          class="aspect-[1108/632] w-[72rem] flex-none bg-gradient-to-br from-[#ff7eb3] via-teal-400 to-[#ff7eb3] opacity-40"
          style="clip-path: polygon(75% 50%, 90% 90%, 100% 55%, 95% 15%, 85% 12%, 70% 35%, 50% 50%, 45% 48%, 40% 30%, 48% 10%, 22% 40%, 2% 5%, 8% 50%, 22% 38%, 60% 100%, 75% 50%)"
        />
      </div>
      <div class="mx-auto max-w-2xl text-center">
        <span class="shadow inline-flex items-center rounded-full bg-white px-4 py-2 mb-8 text-base font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset">
          <HeartIcon
            class="size-6 mr-2 fill-teal-500"
            aria-hidden="true"
          />
          {{ t('home.reviews.tag') }}
        </span>
        <h2 class="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
          {{ t('home.reviews.title') }}
        </h2>
        <p class="mt-6 text-lg/8 text-gray-600">
          {{ t('home.reviews.description') }}
        </p>
      </div>
      <div class="isolate mx-auto mt-10 grid grid-cols-1 gap-8 max-w-2xl">
        <ul class="mx-auto grid w-full max-w-xl grid-cols-2 sm:grid-cols-2 md:grid-cols-3 items-center gap-y-4 gap-x-4 sm:gap-y-8">
          <li
            v-for="testimonial in testimonials"
            :key="testimonial.name"
          >
            <a
              href="#"
              @click.prevent="openTestimonialVideoModal(testimonial.videoUrl)"
            >
              <span class="sr-only">{{ testimonial.name }}</span>
              <img
                class="grayscale hover:grayscale-0 aspect-14/13 w-full rounded-2xl object-cover max-w-none"
                :src="testimonial.imageUrl"
                :alt="testimonial.name"
              >
            </a>
            <button
              type="button"
              class="mt-4 mx-auto flex items-center gap-x-1.5 rounded-md border-2 border-transparent px-3 py-2 text-sm font-semibold hover:text-teal-600 shadow-xs hover:border-teal-600 focus:border-teal-600 focus:outline-none"
              @click="openTestimonialVideoModal(testimonial.videoUrl)"
            >
              {{ t('home.reviews.watch') }}
              <PlayCircleIcon
                class="-mr-0.5 size-5"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </div>
      <div class="mt-8 flex">
        <p class="mx-auto relative rounded-md text-center px-4 py-1.5 text-sm/8 text-gray-600 ring-1 ring-gray-900/10 ring-inset hover:ring-gray-900/20">
          <span class="sm:inline">
            {{ t('home.reviews.cta') }}
          </span>
          <a
            href="#"
            @click.prevent="navigateWhatsapp(t('home.reviews.message'))"
          >
            <span class="ml-2 font-semibold text-teal-500">
              {{ t('home.reviews.contact') }}  <span aria-hidden="true">&rarr;</span></span>
          </a>
        </p>
      </div>
    </div>
    <TransitionRoot
      as="template"
      :show="showTestimonialModal"
    >
      <Dialog
        class="relative z-10"
        @close="closeModal"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white p-4 text-left shadow-xl transition-all sm:my-8 flex flex-col">
                <div class="relative w-full aspect-video">
                  <video
                    class="w-full max-w-md sm:max-w-lg aspect-[9/16]"
                    controls
                    autoplay
                    muted
                    playsinline
                  >
                    <source
                      :src="activeTestimonialVideoURL"
                      type="video/mp4"
                    >
                  </video>
                </div>
                <div class="mt-4 text-center">
                  <button
                    type="button"
                    class="rounded-full bg-gray-600 p-1.5 text-white shadow-xs hover:bg-gray-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                    @click="closeModal"
                  >
                    <XMarkIcon
                      class="size-6 text-gray-200"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- ============ PRICING ============ -->
    <div
      id="pricing"
      class="mx-auto pt-24 max-w-7xl px-6 lg:px-8"
    >
      <div class="mx-auto max-w-2xl lg:mx-0">
        <span class="shadow inline-flex items-center rounded-full bg-white px-4 py-2 mb-8 text-base font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset">
          <BanknotesIcon
            class="size-6 mr-2 fill-teal-500"
            aria-hidden="true"
          />
          {{ t('home.pricing.tag') }}
        </span>
        <h2 class="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
          {{ t('home.pricing.title') }}
        </h2>
        <p class="mt-6 text-lg/8 text-gray-600">
          {{ t('home.pricing.description') }}
        </p>
      </div>

      <div class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        <div
          v-for="tier in tiers"
          :key="tier.name"
          :class="[tier.mostPopular ? 'ring-2 ring-teal-600' : 'ring-1 ring-gray-200', 'rounded-3xl p-8 xl:p-10 bg-white shadow-xl']"
        >
          <div class="flex items-center justify-between gap-x-4">
            <h3
              :id="tier.id"
              :class="[tier.mostPopular ? 'text-teal-600' : 'text-gray-900', 'text-lg/8 font-semibold']"
            >
              {{ tier.name }}
            </h3>
            <p
              v-if="tier.mostPopular"
              class="rounded-full bg-teal-600/10 px-2.5 py-1 text-xs/5 font-semibold text-teal-600"
            >
              {{ t('home.pricing.mostPopular') }}
            </p>
          </div>
          <p class="mt-4 text-sm/8 text-gray-600">
            {{ tier.description }}
          </p>
          <p class="mt-6 flex items-baseline gap-x-1">
            <span class="text-4xl font-semibold tracking-tight text-gray-900">{{ tier.price }}</span>
            <span class="text-sm/8 font-semibold text-gray-600">{{ tier.frequency }}</span>
          </p>
          <a
            :href="tier.href"
            :aria-describedby="tier.id"
            :class="[tier.mostPopular ? 'bg-teal-600 text-white shadow-xs hover:bg-teal-500' : 'text-teal-600 ring-1 ring-teal-500 ring-inset hover:ring-teal-600', 'mt-6 block rounded-md px-3 py-2 text-center text-sm/8 font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600']"
          >
            {{ tier.button }}
          </a>
          <ul class="mt-8 space-y-3 text-sm/8 text-gray-600 xl:mt-10">
            <li
              v-for="feature in tier.features.split(';')"
              :key="feature"
              class="flex gap-x-3"
            >
              <CheckIcon
                class="h-6 w-5 flex-none text-teal-500"
                aria-hidden="true"
              />
              {{ feature }}
            </li>
          </ul>
        </div>
      </div>
      <div class="flex flex-col mt-8 items-start gap-x-8 gap-y-6 rounded-3xl p-8 ring-1 ring-gray-900/10 sm:gap-y-10 sm:p-10 lg:col-span-2 lg:flex-row lg:items-center">
        <div class="lg:min-w-0 lg:flex-1">
          <h3 class="text-base/7 font-semibold">
            {{ t('home.pricing.custom.title') }}
          </h3>
          <p class="mt-1 text-base/7 text-gray-600">
            {{ t('home.pricing.custom.description') }}
          </p>
        </div>
        <a
          href="#"
          class="rounded-md px-3.5 py-2 text-sm/8 font-semibold text-teal-600 ring-1 ring-teal-600 ring-inset hover:ring-teal-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          @click.prevent="navigateWhatsapp(t('home.pricing.custom.message'))"
        >
          {{ t('home.pricing.custom.cta') }} <span aria-hidden="true">&rarr;</span>
        </a>
      </div>
    </div>

    <!-- ============ FAQ ============ -->
    <div
      id="faq"
      class="relative mx-auto pt-24 max-w-7xl px-6 lg:px-8"
    >
      <div class="mx-auto max-w-2xl lg:mx-0">
        <span class="shadow inline-flex items-center rounded-full bg-white px-4 py-2 mb-8 text-base font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset">
          <ChatBubbleLeftEllipsisIcon
            class="size-6 mr-2 fill-teal-500"
            aria-hidden="true"
          />
          {{ t('home.faq.tag') }}
        </span>
        <h2 class="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
          {{ t('home.faq.title') }}
        </h2>
        <p class="mt-6 text-lg/8 text-gray-600">
          {{ t('home.faq.description') }}
        </p>
      </div>

      <div class="isolate mx-auto max-w-7xl grid grid-cols-1 gap-8 lg:mx-0 lg:max-w-none mt-8">
        <dl class="divide-y divide-gray-900/10">
          <Disclosure
            v-for="faq in faqs"
            :key="faq.question"
            v-slot="{ open }"
            as="div"
            class="py-6 first:pt-0 last:pb-0"
          >
            <dt>
              <DisclosureButton class="flex w-full items-start justify-between text-left text-gray-900">
                <span class="text-base/7 font-semibold">{{ faq.question }}</span>
                <span class="ml-6 flex h-7 items-center">
                  <PlusSmallIcon
                    v-if="!open"
                    class="size-6"
                    aria-hidden="true"
                  />
                  <MinusSmallIcon
                    v-else
                    class="size-6"
                    aria-hidden="true"
                  />
                </span>
              </DisclosureButton>
            </dt>
            <DisclosurePanel
              as="dd"
              class="mt-2 pr-12"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p
                v-for="answer in faq.answers"
                :key="answer"
                class="text-base/7 text-gray-600"
                v-html="answer"
              />
            </DisclosurePanel>
          </Disclosure>
        </dl>
      </div>
    </div>

    <!-- ============ FINAL CTA ============ -->
    <div class="relative mx-auto pt-24 pb-8 max-w-7xl px-6 lg:px-8">
      <div class="ll-cta-panel ll-reveal">
        <h2 class="text-3xl sm:text-4xl font-semibold tracking-tight text-balance text-white text-center max-w-2xl mx-auto relative">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="t('home.cta.title')" />
        </h2>
        <p class="mx-auto mt-6 text-lg/8 text-pretty text-teal-50 text-center max-w-xl relative">
          {{ t('home.cta.description') }}
        </p>
        <div class="mt-10 flex items-center justify-center gap-x-6 relative">
          <a
            :href="signupLink()"
            class="rounded-xl bg-white px-6 py-3 text-sm font-semibold text-teal-700 shadow-xl hover:bg-teal-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {{ t('home.cta.cta') }}
          </a>
          <a
            href="#"
            class="text-sm font-semibold text-white"
            @click.prevent="navigateWhatsapp(t('home.reviews.message'))"
          >
            {{ t('home.cta.contact') }} <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    </div>
  </main>

  <!-- Footer -->
  <footer>
    <div class="mx-auto max-w-7xl overflow-hidden px-6 py-20 sm:py-24 lg:px-8">
      <nav
        class="-mb-6 flex flex-wrap justify-center gap-x-12 gap-y-3 text-sm/8"
        aria-label="Footer"
      >
        <a
          v-for="item in footerNavigation.main"
          :key="item.name"
          :href="item.href"
          class="text-gray-600 hover:text-gray-900"
        >{{ item.name }}</a>
      </nav>
      <div class="mt-16 flex justify-center gap-x-10">
        <a
          v-for="item in footerNavigation.social"
          :key="item.name"
          :href="item.href"
          class="text-gray-600 hover:text-gray-800"
        >
          <span class="sr-only">{{ item.name }}</span>
          <component
            :is="item.icon"
            class="size-6"
            aria-hidden="true"
          />
        </a>
      </div>
      <div class="flex justify-center">
        <ChooseLanguage />
      </div>
      <p class="mt-8 text-sm/8 text-gray-600 text-center">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="t('home.footer.love')" />
      </p>
    </div>
  </footer>
</template>

<script setup lang="ts">
import {
  defineComponent, h, ref, computed, onBeforeMount, onMounted, onUnmounted, nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useAuthStore from '@/store/auth';
import HomeHeader from '@/components/tailwind/header/HomeHeader.vue';
import ChooseLanguage from '@/components/tailwind/shared/ChooseLanguage.vue';
import {
  SparklesIcon,
  BanknotesIcon,
  HeartIcon,
  PlayCircleIcon,
  XMarkIcon,
  ChatBubbleLeftEllipsisIcon,
  MinusSmallIcon,
  PlusSmallIcon,
  CheckBadgeIcon,
} from '@heroicons/vue/24/outline';
import { CheckIcon } from '@heroicons/vue/20/solid';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';

import BrechoEraMeuImage from '@/assets/images/customers/brechoerameu.jpg';
import JazminMais from '@/assets/images/customers/jazminmais.jpeg';
import FeiraDoLar from '@/assets/images/customers/feiradolar.jpeg';
import ElaineBrechoeOutlet from '@/assets/images/customers/elainebrechoeoutlet.jpg';
import ThomyTalks from '@/assets/images/customers/thomytalks.jpg';
import LuzdaLuaJoacaba from '@/assets/images/customers/luzdaluajoacaba.jpg';
import TaueTauBrecho from '@/assets/images/customers/tau.e.tau.brecho.jpg';
import ElvisCalcados from '@/assets/images/customers/elviscalcados.jpg';
import Lucimarmodascx from '@/assets/images/customers/lucimarmodascx.jpg';
import Elahmodafeminina from '@/assets/images/customers/elahmodafeminina.jpg';
import NovadonaMogi from '@/assets/images/customers/novadona_mogi.jpg';
import Pequenoluxo from '@/assets/images/customers/pequenoluxo.jpg';
import LojatransitoOficial from '@/assets/images/customers/lojatransito.oficial.png';
import BingaPresentes from '@/assets/images/customers/bingapresentes.jpg';
import Portobelobrechooutlet from '@/assets/images/customers/portobelobrechooutlet.jpg';

const { t, locale } = useI18n();
const { isAuthenticated } = useAuthStore();
const router = useRouter();

/*
  Data
*/
const dataLoaded = ref(false);
const pageRoot = ref<HTMLElement | null>(null);
const showTestimonialModal = ref(false);
const activeTestimonialVideoURL = ref('');

/* Hero mockup animation state */
const liveComments = ref<Array<{ id: number; name: string; color: string; text: string }>>([]);
// Seeded with the steady-state values so the numbers read
// "healthy live" from the first frame — mobile visitors used to
// see 24 / 7 / 7 (barely-a-live) because they scroll past the
// hero before the tick loop had accumulated any activity.
// The tick loop still increments on top, so users watching
// longer see the numbers keep climbing.
const mockStats = ref({ msg: 340, cart: 85, dm: 85 });
const commentDefs = [
  { name: 'maria.moda', color: '#f472b6', key: 'c0' },
  { name: 'ju_semijoias', color: '#a78bfa', key: 'c1' },
  { name: 'carla.brecho', color: '#fbbf24', key: 'c2' },
  { name: 'dona.rosa', color: '#34d399', key: 'c3' },
  { name: 'bel.presentes', color: '#f87171', key: 'c4' },
  { name: 'lu.acessorios', color: '#60a5fa', key: 'c5' },
];
let commentIdx = 0;
let commentTimer: ReturnType<typeof setInterval> | undefined;
let revealObserver: IntersectionObserver | undefined;
let countObserver: IntersectionObserver | undefined;
let spotObserver: IntersectionObserver | undefined;

const waveHeights = [30, 55, 80, 45, 70, 35, 60, 85, 50, 75, 40, 65, 90, 45, 60, 30, 70, 55];

const customers = [
  { image: BingaPresentes, name: 'bingapresentes', href: 'https://instagram.com/bingapresentes' },
  { image: BrechoEraMeuImage, name: 'brechoerameuicara', href: 'https://instagram.com/brechoerameuicara' },
  { image: ElaineBrechoeOutlet, name: 'elainebrechoeoutlet', href: 'https://instagram.com/elainebrechoeoutlet' },
  { image: Elahmodafeminina, name: 'elahmodafeminina', href: 'https://instagram.com/elahmodafeminina' },
  { image: ElvisCalcados, name: 'elviscalcados', href: 'https://instagram.com/elviscalcados' },
  { image: FeiraDoLar, name: 'feiradolar', href: 'https://instagram.com/feiradolar' },
  { image: JazminMais, name: 'jazminmais', href: 'https://instagram.com/jazminmais' },
  { image: LuzdaLuaJoacaba, name: 'luzdaluajoaçaba', href: 'https://instagram.com/luzdaluajoacaba' },
  { image: LojatransitoOficial, name: 'lojatransito.oficial', href: 'https://instagram.com/lojatransito.oficial' },
  { image: Lucimarmodascx, name: 'lucimarmodascx', href: 'https://instagram.com/lucimarmodascx' },
  { image: NovadonaMogi, name: 'novadona_mogi', href: 'https://instagram.com/novadona_mogi' },
  { image: Pequenoluxo, name: 'pequenoluxo', href: 'https://instagram.com/pequenoluxo' },
  { image: Portobelobrechooutlet, name: 'portobelobrechooutlet', href: 'https://instagram.com/portobelobrechooutlet' },
  { image: TaueTauBrecho, name: 'tau.e.tau.brechó', href: 'https://instagram.com/tau.e.tau.brecho' },
  { image: ThomyTalks, name: 'thomytalks', href: 'https://instagram.com/thomytalks' },
];
const testimonials = [
  {
    id: 1,
    name: '@brechoerameuicara',
    href: 'https://instagram.com/brechoerameuicara',
    videoUrl: 'https://ucarecdn.com/7828318d-7073-4374-9679-d4d35dc4a38b/brechoerameuicara.mp4',
    imageUrl: BrechoEraMeuImage,
    location: 'Içara - SC',
  },
  {
    id: 2,
    name: '@feiradolar',
    href: 'https://instagram.com/feiradolar',
    videoUrl: 'https://ucarecdn.com/ef164475-214e-44e0-baf2-951907cbe807/feiradolar.mp4',
    imageUrl: FeiraDoLar,
    location: 'São José do Rio Pardo - SP',
  },
  {
    id: 3,
    name: '@jazminmais',
    href: 'https://instagram.com/jazminmais',
    videoUrl: 'https://ucarecdn.com/e291fa61-3d8e-44bf-ad1d-e6b9837aa6d8/jazminmais.mp4',
    imageUrl: JazminMais,
    location: 'Pinheiros - SP',
  },
];

/*
  Computed
*/
const landingStats = computed(() => [
  {
    label: t('home.stats.reports.title'),
    target: 11,
    decimals: 0,
    suffix: t('home.landing.stats.thousand'),
  },
  {
    label: t('home.stats.messages.title'),
    target: 5,
    decimals: 1,
    suffix: t('home.landing.stats.million'),
  },
  {
    label: t('home.stats.hours.title'),
    target: 40,
    decimals: 0,
    suffix: t('home.landing.stats.thousand'),
  },
  {
    label: t('home.stats.accounts.title'),
    target: 1,
    decimals: 0,
    suffix: t('home.landing.stats.thousand'),
  },
]);
const journeySteps = computed(() => [
  {
    emoji: '📱',
    bg: 'linear-gradient(135deg,#fdf2f8,#fce7f3)',
    color: '#db2777',
    title: t('home.landing.journey.s1t'),
    desc: t('home.landing.journey.s1d'),
  },
  {
    emoji: '🎥',
    bg: 'linear-gradient(135deg,#fff1f2,#ffe4e6)',
    color: '#e11d48',
    title: t('home.landing.journey.s2t'),
    desc: t('home.landing.journey.s2d'),
  },
  {
    emoji: '✨',
    bg: 'linear-gradient(135deg,#f5f3ff,#ede9fe)',
    color: '#7c3aed',
    title: t('home.landing.journey.s3t'),
    desc: t('home.landing.journey.s3d'),
  },
  {
    emoji: '🛒',
    bg: 'linear-gradient(135deg,#f0fdfa,#ccfbf1)',
    color: '#0f766e',
    title: t('home.landing.journey.s4t'),
    desc: t('home.landing.journey.s4d'),
  },
]);
const crmSegments = computed(() => [
  { label: t('home.landing.spots.crm.seg1'), pct: 18, bg: 'linear-gradient(90deg,#8b5cf6,#a78bfa)' },
  { label: t('home.landing.spots.crm.seg2'), pct: 34, bg: 'linear-gradient(90deg,#0d9488,#14b8a6)' },
  { label: t('home.landing.spots.crm.seg3'), pct: 31, bg: 'linear-gradient(90deg,#f59e0b,#fbbf24)' },
  { label: t('home.landing.spots.crm.seg4'), pct: 17, bg: '#d1d5db' },
]);
const faqs = computed(() => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => ({
  question: t(`home.faq.questions.${i}.question`),
  answers: t(`home.faq.questions.${i}.answers`).split('\n'),
})));
const tiers = computed(() => [
  {
    name: t('home.pricing.tiers.basic.title'),
    button: t('home.pricing.tiers.basic.button'),
    id: 'tier-basic',
    href: router.resolve({ name: 'SessionSignupPage' }).href,
    price: t('home.pricing.tiers.basic.price'),
    frequency: t('home.pricing.tiers.basic.frequency'),
    description: t('home.pricing.tiers.basic.description'),
    features: t('home.pricing.tiers.basic.features'),
    mostPopular: false,
  },
  {
    name: t('home.pricing.tiers.intermediate.title'),
    button: t('home.pricing.tiers.intermediate.button'),
    id: 'tier-intermediate',
    href: router.resolve({ name: 'SessionSignupPage' }).href,
    price: t('home.pricing.tiers.intermediate.price'),
    frequency: t('home.pricing.tiers.intermediate.frequency'),
    description: t('home.pricing.tiers.intermediate.description'),
    features: t('home.pricing.tiers.intermediate.features'),
    mostPopular: false,
  },
  {
    name: t('home.pricing.tiers.subscription.title'),
    button: t('home.pricing.tiers.subscription.button'),
    id: 'tier-subscription',
    href: router.resolve({ name: 'SessionSignupPage' }).href,
    price: t('home.pricing.tiers.subscription.price'),
    frequency: t('home.pricing.tiers.subscription.frequency'),
    description: t('home.pricing.tiers.subscription.description'),
    features: t('home.pricing.tiers.subscription.features'),
    mostPopular: true,
  },
]);
const footerNavigation = computed(() => ({
  main: [
    { name: t('footer.links.terms.shoppers.name'), href: router.resolve({ name: 'TermsAndConditionsPageShoppers' }).href },
    { name: t('footer.links.terms.partners.name'), href: router.resolve({ name: 'TermsAndConditionsPage' }).href },
    { name: t('footer.links.terms.privacy.name'), href: router.resolve({ name: 'PrivacyPage' }).href },
  ],
  social: [
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/livelinkoficial',
      icon: defineComponent({
        render: () => h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
          h('path', {
            'fill-rule': 'evenodd',
            d: 'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z',
            'clip-rule': 'evenodd',
          }),
        ]),
      }),
    },
  ],
}));

/*
  Methods
*/
const signupLink = () => router.resolve({ name: 'SessionSignupPage' }).href;
const navigateWhatsapp = (message: string) => {
  window.open(`https://wa.me/+5511994991786?text=${message}`, '_blank');
};
const closeModal = () => {
  showTestimonialModal.value = false;
};
const openTestimonialVideoModal = (videoUrl: string) => {
  activeTestimonialVideoURL.value = videoUrl;
  showTestimonialModal.value = true;
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const pushComment = () => {
  const def = commentDefs[commentIdx % commentDefs.length];
  commentIdx += 1;
  const text = t(`home.landing.mock.comments.${def.key}`);
  liveComments.value.push({
    id: commentIdx, name: def.name, color: def.color, text,
  });
  while (liveComments.value.length > 4) liveComments.value.shift();
  mockStats.value.msg += 1 + Math.floor(Math.random() * 3);
  if (/quero|want/i.test(text)) {
    mockStats.value.cart += 1;
    mockStats.value.dm += 1;
  }
};

const startHeroLoop = () => {
  if (prefersReducedMotion()) {
    for (let i = 0; i < 3; i += 1) pushComment();
    mockStats.value = { msg: 340, cart: 85, dm: 85 };
    return;
  }
  pushComment();
  commentTimer = setInterval(pushComment, 2200);
};

const animateCount = (el: HTMLElement) => {
  const node = el;
  const target = parseFloat(node.dataset.target || '0');
  const decimals = parseInt(node.dataset.decimals || '0', 10);
  const suffix = node.dataset.suffix || '';
  const loc = locale.value === 'en' ? 'en-US' : 'pt-BR';
  const fmt = (v: number) => (decimals
    ? v.toFixed(decimals).replace('.', loc === 'pt-BR' ? ',' : '.')
    : Math.round(v).toLocaleString(loc));
  if (prefersReducedMotion()) {
    node.textContent = fmt(target) + suffix;
    return;
  }
  const t0 = performance.now();
  const dur = 1400;
  const tick = (now: number) => {
    const p = Math.min((now - t0) / dur, 1);
    const ease = 1 - (1 - p) ** 4;
    node.textContent = fmt(target * ease) + suffix;
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};

// Reveal-on-scroll pattern is inherently fragile: `.ll-reveal` starts
// at opacity:0 and only becomes visible when the IntersectionObserver
// adds `.ll-visible`. Any glitch (initial-dispatch race, missed layout,
// observer set up before an element is in the DOM, IntersectionObserver
// unavailable, or `pageRoot` unbound on first call) leaves that section
// permanently blank — the "sometimes 'Nossos números' and 'Como
// funciona' are empty" reports were reproductions of exactly that.
// The two safety nets below make revealing eventual-and-guaranteed.
const revealAll = () => {
  pageRoot.value?.querySelectorAll('.ll-reveal').forEach((el) => {
    el.classList.add('ll-visible');
  });
};

const initObservers = () => {
  const root = pageRoot.value;
  // If the ref hasn't bound yet (rare race with v-if + nextTick),
  // retry on the next frame. Skips the silent no-op that used to
  // brick the entire below-the-fold when this hit.
  if (!root) {
    requestAnimationFrame(initObservers);
    return;
  }

  // Fallback #1: if IntersectionObserver isn't available (very old
  // browsers) reveal everything immediately.
  if (typeof IntersectionObserver === 'undefined') {
    revealAll();
    return;
  }

  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('ll-visible');
        revealObserver?.unobserve(e.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
  root.querySelectorAll('.ll-reveal').forEach((el) => revealObserver?.observe(el));

  // Fallback #2: after 1.5s, force-reveal anything still hidden.
  // If the observer worked, this is a no-op (elements already have
  // `.ll-visible`). If it didn't, the seller sees the section instead
  // of a blank strip. Chosen over shorter delays so the intended
  // scroll animation still plays on normal loads.
  setTimeout(revealAll, 1500);

  countObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        countObserver?.unobserve(e.target);
        animateCount(e.target as HTMLElement);
      }
    });
  }, { threshold: 0.6 });
  root.querySelectorAll('.ll-count').forEach((el) => countObserver?.observe(el));

  spotObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      e.target.classList.toggle('ll-play', e.isIntersecting);
    });
  }, { threshold: 0.3 });
  root.querySelectorAll('.ll-spot-anim').forEach((el) => spotObserver?.observe(el));
};

/*
  Hooks
*/
onMounted(() => {
  setTimeout(() => {
    dataLoaded.value = true;
    nextTick(() => {
      initObservers();
      startHeroLoop();
    });
  }, 100);
});
onBeforeMount(() => {
  if (isAuthenticated()) {
    router.push({
      name: 'DashboardPage',
    });
  }
});
onUnmounted(() => {
  if (commentTimer) clearInterval(commentTimer);
  revealObserver?.disconnect();
  countObserver?.disconnect();
  spotObserver?.disconnect();
});
</script>

<style scoped>
/* ============ LIVELINK landing animations ============
   Motion: ease-out-quart / ease-out-expo, 100-300ms UI, no bounce.
   All animations disabled under prefers-reduced-motion. */
.ll-hero-bg {
  background:
    radial-gradient(42rem 30rem at 82% -8%, rgba(20, 184, 166, 0.14), transparent 62%),
    radial-gradient(36rem 26rem at -10% 30%, rgba(238, 42, 123, 0.07), transparent 60%),
    linear-gradient(#fff, #fff);
}
.ll-hero-grid {
  background-image:
    linear-gradient(rgba(17, 24, 39, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(17, 24, 39, 0.045) 1px, transparent 1px);
  background-size: 44px 44px;
  -webkit-mask-image: radial-gradient(38rem 30rem at 70% 10%, #000 30%, transparent 75%);
  mask-image: radial-gradient(38rem 30rem at 70% 10%, #000 30%, transparent 75%);
}
.ll-hl svg {
  position: absolute;
  left: 0;
  bottom: -0.35rem;
  width: 100%;
  height: 0.55rem;
  overflow: visible;
}
.ll-hl svg path {
  stroke: #14b8a6;
  stroke-width: 4;
  fill: none;
  stroke-linecap: round;
  opacity: 0.55;
}
.ll-meta-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
}

/* ---- Hero visual ---- */
.ll-hero-visual {
  position: relative;
  min-height: 34rem;
}
@media (max-width: 1023px) {
  .ll-hero-visual {
    min-height: 31rem;
    max-width: 30rem;
    margin: 0 auto;
    width: 100%;
  }
}
.ll-phone {
  position: absolute;
  left: 0;
  top: 1.2rem;
  width: 16.2rem;
  height: 31rem;
  border-radius: 2.2rem;
  background: linear-gradient(160deg, #1e293b, #0f172a);
  box-shadow:
    0 24px 60px -18px rgba(15, 23, 42, 0.45),
    inset 0 0 0 6px #0f172a,
    inset 0 0 0 7px rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.ll-phone-screen {
  position: absolute;
  inset: 6px;
  border-radius: 1.85rem;
  overflow: hidden;
  background: linear-gradient(200deg, #f472b6 0%, #ec4899 22%, #a855f7 55%, #6366f1 100%);
}
.ll-phone-screen::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(18rem 14rem at 50% 15%, rgba(255, 255, 255, 0.28), transparent 60%);
}
.ll-live-topbar {
  position: absolute;
  top: 0.9rem;
  left: 0.9rem;
  right: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 3;
}
.ll-live-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #f9ce34, #ee2a7b 55%, #6228d7);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85);
}
.ll-live-handle {
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}
.ll-live-pill {
  margin-left: auto;
  background: #f43f5e;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.18rem 0.5rem;
  border-radius: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}
.ll-dot-w {
  width: 0.4rem;
  height: 0.4rem;
  background: #fff;
  border-radius: 50%;
  animation: ll-pulse 1.6s infinite;
}
.ll-viewers {
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 600;
  padding: 0.18rem 0.5rem;
  border-radius: 0.4rem;
}
@keyframes ll-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
.ll-comments {
  position: absolute;
  left: 0.9rem;
  right: 3.2rem;
  bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 3;
}
.ll-comment {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  animation: ll-comment-in 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.ll-c-av {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  flex: none;
  margin-top: 0.1rem;
}
.ll-c-body {
  color: #fff;
  font-size: 0.68rem;
  line-height: 1.35;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}
.ll-c-body b {
  display: block;
  font-size: 0.62rem;
  opacity: 0.85;
  font-weight: 600;
}
@keyframes ll-comment-in {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}
.ll-hearts {
  position: absolute;
  right: 0.7rem;
  bottom: 1rem;
  width: 2rem;
  height: 12rem;
  z-index: 2;
  pointer-events: none;
}
.ll-heart {
  position: absolute;
  bottom: 0;
  left: 50%;
  font-size: 1rem;
  opacity: 0;
  animation: ll-float-up 3.2s linear infinite;
}
.ll-heart:nth-child(2) { animation-delay: 0.9s; font-size: 0.8rem; }
.ll-heart:nth-child(3) { animation-delay: 1.7s; font-size: 1.15rem; }
.ll-heart:nth-child(4) { animation-delay: 2.4s; font-size: 0.75rem; }
@keyframes ll-float-up {
  0% { opacity: 0; transform: translate(-50%, 0) scale(0.6); }
  12% { opacity: 0.95; }
  100% { opacity: 0; transform: translate(calc(-50% + 10px), -11rem) scale(1.1); }
}
.ll-report-card {
  position: absolute;
  right: 0;
  top: 4.6rem;
  width: 19.5rem;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 24px 55px -20px rgba(17, 24, 39, 0.28);
  border: 1px solid #e5e7eb;
  border-top: 4px solid #14b8a6;
  padding: 1.1rem 1.2rem;
  z-index: 4;
}
.ll-report-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.8rem;
}
.ll-vis-title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #9ca3af;
  text-transform: uppercase;
}
.ll-pill-teal,
.ll-pill-pink,
.ll-pill-violet,
.ll-pill-blue {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.62rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.16rem 0.55rem;
}
.ll-pill-teal { color: #0f766e; background: #f0fdfa; border: 1px solid #99f6e4; }
.ll-pill-pink { color: #be185d; background: #fdf2f8; border: 1px solid #fbcfe8; }
.ll-pill-violet { color: #6d28d9; background: #f5f3ff; border: 1px solid #ddd6fe; }
.ll-pill-blue { color: #1d4ed8; background: #eff6ff; border: 1px solid #bfdbfe; }
.ll-dot-pulse {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: currentColor;
  animation: ll-pulse 1.6s infinite;
}
.ll-report-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.9rem;
}
.ll-rstat {
  background: #f9fafb;
  border-radius: 0.7rem;
  padding: 0.55rem 0.6rem;
}
.ll-rstat b {
  display: block;
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
  font-variant-numeric: tabular-nums;
}
.ll-rstat span {
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #6b7280;
  text-transform: uppercase;
}
.ll-report-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.55rem;
  border-radius: 0.7rem;
  opacity: 0;
  animation: ll-row-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
@keyframes ll-row-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.ll-report-row + .ll-report-row { margin-top: 0.25rem; }
.ll-r-ic {
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 0.55rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex: none;
}
.ll-r-tx {
  font-size: 0.72rem;
  line-height: 1.3;
  color: #4b5563;
}
.ll-r-tx b { color: #111827; font-weight: 600; }
.ll-r-badge {
  margin-left: auto;
  font-size: 0.58rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  flex: none;
}
.ll-bg-teal { background: #f0fdfa; color: #0f766e; }
.ll-bg-amber { background: #fffbeb; color: #b45309; }
.ll-bg-violet { background: #f5f3ff; color: #6d28d9; }
.ll-bg-rose { background: #fff1f2; color: #be123c; }
.ll-dm-chip {
  position: absolute;
  left: 6.5rem;
  bottom: 1.4rem;
  z-index: 5;
  display: flex;
  gap: 0.6rem;
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.9rem;
  box-shadow: 0 18px 40px -14px rgba(17, 24, 39, 0.3);
  padding: 0.7rem 0.9rem;
  width: 16.5rem;
  animation: ll-bob 5s cubic-bezier(0.25, 1, 0.5, 1) infinite alternate;
}
@keyframes ll-bob {
  from { transform: translateY(0); }
  to { transform: translateY(-10px); }
}
.ll-dm-ic {
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 0.65rem;
  background: linear-gradient(135deg, #f9ce34, #ee2a7b 55%, #6228d7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  flex: none;
}
.ll-dm-tx {
  font-size: 0.7rem;
  line-height: 1.35;
  color: #4b5563;
}
.ll-dm-tx b { color: #111827; }
.ll-dm-check {
  margin-left: auto;
  color: #0d9488;
  font-weight: 700;
  font-size: 0.85rem;
  flex: none;
}
@media (max-width: 420px) {
  .ll-phone { width: 14rem; height: 27rem; }
  .ll-report-card { width: 16.6rem; padding: 0.95rem 1rem; top: 3.4rem; }
  .ll-dm-chip { left: auto; right: 0.25rem; bottom: 0.4rem; width: 15.5rem; }
}

/* ---- Section tag ---- */
.ll-section-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #0f766e;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  margin-bottom: 1.1rem;
}

/* ---- Stat cards ---- */
.ll-stat-card {
  transition:
    transform 0.2s cubic-bezier(0.25, 1, 0.5, 1),
    box-shadow 0.2s cubic-bezier(0.25, 1, 0.5, 1);
}
.ll-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px -8px rgba(17, 24, 39, 0.14);
}

/* ---- Journey ---- */
.ll-step-card {
  position: relative;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.7rem 1.4rem 1.5rem;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
  transition:
    transform 0.25s cubic-bezier(0.25, 1, 0.5, 1),
    box-shadow 0.25s cubic-bezier(0.25, 1, 0.5, 1),
    border-color 0.25s cubic-bezier(0.25, 1, 0.5, 1);
}
.ll-step-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px -8px rgba(17, 24, 39, 0.14);
  border-color: #99f6e4;
}
.ll-step-num {
  position: absolute;
  top: -0.85rem;
  left: 1.3rem;
  background: #0d9488;
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 0.55rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 14px -4px rgba(13, 148, 136, 0.55);
}
.ll-step-ic {
  width: 2.9rem;
  height: 2.9rem;
  border-radius: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin-bottom: 1rem;
}
/* Livvia's signature dark-teal "green bg" — identical to LivviaStrip and
   the plugin-dashboard hero, so the green banner *is* Livvia everywhere. */
.ll-livvia-strip {
  margin: 3rem auto 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background:
    radial-gradient(16rem 12rem at 4% 20%, rgba(192, 38, 211, 0.22), transparent 65%),
    radial-gradient(26rem 16rem at 85% -20%, rgba(255, 255, 255, 0.16), transparent 60%),
    linear-gradient(135deg, #134e4a 0%, #115e59 45%, #0f766e 100%);
  border-radius: 1rem;
  box-shadow: 0 18px 40px -20px rgba(13, 148, 136, 0.45);
  padding: 1.4rem 1.6rem;
  max-width: 44rem;
}
.ll-livvia-name { color: #e9d5ff; }
.ll-livvia-av {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  flex: none;
  position: relative;
  background: linear-gradient(135deg, #9333ea, #c026d3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 1.15rem;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25), 0 8px 18px -6px rgba(0, 0, 0, 0.35);
}
.ll-livvia-av::after {
  content: '';
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background: #4ade80;
  border: 2.5px solid #0f766e;
}
.ll-livvia-bubble {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.35rem 1rem 1rem 1rem;
  padding: 0.85rem 1.05rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
}
.ll-livvia-bubble :deep(b) { color: #fff; }

/* ---- Spotlights ---- */
.ll-spot {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3.5rem;
  align-items: center;
  padding: 3.4rem 0;
  text-align: left;
}
.ll-spot + .ll-spot { border-top: 1px dashed #d1d5db; }
@media (min-width: 1024px) {
  .ll-spot.ll-flip > div:last-child { order: -1; }
}
@media (max-width: 1023px) {
  .ll-spot { grid-template-columns: 1fr; gap: 2rem; padding: 2.6rem 0; }
}
.ll-spot-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 0.3rem 0.8rem;
  margin-bottom: 1rem;
}
.ll-spot h3 {
  font-size: clamp(1.45rem, 2.4vw, 1.9rem);
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.7rem;
  line-height: 1.2;
  letter-spacing: -0.01em;
}
.ll-desc { color: #6b7280; font-size: 1rem; font-weight: 500; }
.ll-checks {
  list-style: none;
  margin: 1.2rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.ll-checks li {
  display: flex;
  gap: 0.6rem;
  font-size: 0.92rem;
  color: #4b5563;
  align-items: flex-start;
}
.ll-checks li::before { content: '✓'; color: #0d9488; font-weight: 700; flex: none; }
.ll-kick-teal { color: #0f766e; background: #f0fdfa; border: 1px solid #99f6e4; }
.ll-kick-pink { color: #be185d; background: #fdf2f8; border: 1px solid #fbcfe8; }
.ll-kick-violet { color: #6d28d9; background: #f5f3ff; border: 1px solid #ddd6fe; }
.ll-kick-blue { color: #1d4ed8; background: #eff6ff; border: 1px solid #bfdbfe; }
.ll-kick-amber { color: #b45309; background: #fffbeb; border: 1px solid #fde68a; }
.ll-vis-panel {
  position: relative;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1.25rem;
  box-shadow: 0 10px 30px -8px rgba(17, 24, 39, 0.14);
  padding: 1.35rem 1.45rem;
  overflow: hidden;
}
.ll-vis-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 1rem;
}

/* Report spotlight */
.ll-vis-tabs { display: flex; gap: 0.3rem; margin-bottom: 1rem; }
.ll-vis-tab {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  padding: 0.3rem 0.7rem;
  border-radius: 0.5rem;
}
.ll-vis-tab.ll-on { background: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; }
.ll-bars {
  display: flex;
  gap: 0.4rem;
  align-items: flex-end;
  height: 6.5rem;
  padding-top: 1.9rem;
}
.ll-bars i {
  flex: 1;
  border-radius: 0.3rem 0.3rem 0 0;
  background: #99f6e4;
  transform: scaleY(0.06);
  transform-origin: bottom;
  transition: transform 0.9s cubic-bezier(0.25, 1, 0.5, 1);
}
.ll-bars i.ll-peak { background: #0d9488; position: relative; }
.ll-bars i.ll-peak::after {
  content: attr(data-peak);
  position: absolute;
  top: -1.85rem;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 0.6rem;
  font-weight: 700;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 0.14rem 0.5rem;
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.25, 1, 0.5, 1) 1s;
}
.ll-play .ll-bars i { transform: scaleY(1); }
.ll-play .ll-bars i.ll-peak::after { opacity: 1; }
.ll-bars i:nth-child(1) { transition-delay: 0.05s; }
.ll-bars i:nth-child(2) { transition-delay: 0.1s; }
.ll-bars i:nth-child(3) { transition-delay: 0.15s; }
.ll-bars i:nth-child(4) { transition-delay: 0.2s; }
.ll-bars i:nth-child(5) { transition-delay: 0.25s; }
.ll-bars i:nth-child(6) { transition-delay: 0.3s; }
.ll-bars i:nth-child(7) { transition-delay: 0.35s; }
.ll-bars i:nth-child(8) { transition-delay: 0.4s; }
.ll-bars i:nth-child(9) { transition-delay: 0.45s; }
.ll-bars i:nth-child(10) { transition-delay: 0.5s; }
.ll-bars i:nth-child(11) { transition-delay: 0.55s; }
.ll-bars i:nth-child(12) { transition-delay: 0.6s; }
.ll-msg-feed {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.ll-msg-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.74rem;
  color: #4b5563;
  opacity: 0;
  transform: translateY(8px);
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}
.ll-msg-row b { color: #111827; }
.ll-m-av { width: 1.4rem; height: 1.4rem; border-radius: 50%; flex: none; }
.ll-m-tag {
  margin-left: auto;
  font-size: 0.58rem;
  font-weight: 700;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  flex: none;
}
.ll-play .ll-msg-row { opacity: 1; transform: translateY(0); }
.ll-play .ll-msg-row:nth-child(1) { transition-delay: 1.1s; }
.ll-play .ll-msg-row:nth-child(2) { transition-delay: 1.35s; }
.ll-play .ll-msg-row:nth-child(3) { transition-delay: 1.6s; }

/* Carts flow spotlight (9s loop) */
.ll-flow {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  min-height: 19rem;
  justify-content: center;
}
.ll-f-el {
  animation-duration: 9s;
  animation-iteration-count: infinite;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  animation-play-state: paused;
  opacity: 0;
}
.ll-play .ll-f-el { animation-play-state: running; }
.ll-f-comment {
  align-self: flex-start;
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  max-width: 85%;
  animation-name: ll-kf-a;
}
.ll-f-av {
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24, #f472b6);
  flex: none;
}
.ll-f-bub {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.3rem 0.9rem 0.9rem 0.9rem;
  padding: 0.55rem 0.8rem;
  font-size: 0.8rem;
  color: #374151;
}
.ll-f-bub b { display: block; font-size: 0.64rem; color: #9ca3af; }
.ll-f-cart {
  align-self: center;
  width: 88%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #14b8a6;
  border-radius: 0.9rem;
  box-shadow: 0 10px 30px -8px rgba(17, 24, 39, 0.14);
  padding: 0.8rem 0.95rem;
  animation-name: ll-kf-b;
}
.ll-fc-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}
.ll-fc-badge {
  margin-left: auto;
  font-size: 0.56rem;
  font-weight: 700;
  color: #0f766e;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
}
.ll-f-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.74rem;
  color: #4b5563;
  padding: 0.28rem 0;
  border-bottom: 1px dashed #e5e7eb;
  opacity: 0;
  animation: ll-kf-c 9s infinite cubic-bezier(0.16, 1, 0.3, 1);
  animation-play-state: paused;
}
.ll-f-item:nth-of-type(2) { animation-name: ll-kf-d; }
.ll-play .ll-f-item { animation-play-state: running; }
.ll-f-total {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  font-weight: 700;
  color: #111827;
  padding-top: 0.4rem;
  opacity: 0;
  animation: ll-kf-e 9s infinite cubic-bezier(0.16, 1, 0.3, 1);
  animation-play-state: paused;
}
.ll-play .ll-f-total { animation-play-state: running; }
.ll-f-dm {
  align-self: flex-end;
  display: flex;
  gap: 0.55rem;
  align-items: center;
  max-width: 92%;
  animation-name: ll-kf-f;
}
.ll-f-dm-bub {
  background: #0d9488;
  color: #fff;
  border-radius: 0.9rem 0.3rem 0.9rem 0.9rem;
  padding: 0.6rem 0.85rem;
  font-size: 0.78rem;
  box-shadow: 0 10px 24px -10px rgba(13, 148, 136, 0.6);
}
.ll-f-ck {
  display: block;
  text-align: right;
  font-size: 0.62rem;
  opacity: 0.85;
  margin-top: 0.15rem;
}
.ll-f-note {
  align-self: flex-end;
  font-size: 0.66rem;
  color: #9ca3af;
  font-weight: 600;
  animation-name: ll-kf-g;
}
@keyframes ll-kf-a {
  0% { opacity: 0; transform: translateY(12px); }
  5%, 88% { opacity: 1; transform: translateY(0); }
  95%, 100% { opacity: 0; }
}
@keyframes ll-kf-b {
  0%, 10% { opacity: 0; transform: translateY(14px) scale(0.97); }
  17%, 88% { opacity: 1; transform: translateY(0) scale(1); }
  95%, 100% { opacity: 0; }
}
@keyframes ll-kf-c {
  0%, 20% { opacity: 0; transform: translateX(-8px); }
  26%, 88% { opacity: 1; transform: translateX(0); }
  95%, 100% { opacity: 0; }
}
@keyframes ll-kf-d {
  0%, 28% { opacity: 0; transform: translateX(-8px); }
  34%, 88% { opacity: 1; transform: translateX(0); }
  95%, 100% { opacity: 0; }
}
@keyframes ll-kf-e {
  0%, 38% { opacity: 0; }
  44%, 88% { opacity: 1; }
  95%, 100% { opacity: 0; }
}
@keyframes ll-kf-f {
  0%, 52% { opacity: 0; transform: translateY(12px); }
  59%, 88% { opacity: 1; transform: translateY(0); }
  95%, 100% { opacity: 0; }
}
@keyframes ll-kf-g {
  0%, 68% { opacity: 0; }
  74%, 88% { opacity: 1; }
  95%, 100% { opacity: 0; }
}
.ll-kw-chips { display: flex; gap: 0.4rem; margin-top: 0.9rem; flex-wrap: wrap; }
.ll-kw-chip {
  font-size: 0.64rem;
  font-weight: 600;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.22rem 0.6rem;
}

/* Transcription spotlight */
.ll-player {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  background: #111827;
  border-radius: 0.8rem;
  padding: 0.65rem 0.85rem;
  margin-bottom: 1rem;
}
.ll-play-btn {
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 50%;
  background: #14b8a6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.7rem;
  flex: none;
}
.ll-wv { display: flex; gap: 2.5px; align-items: center; height: 1.9rem; flex: 1; }
.ll-wv i {
  width: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.45);
  animation: ll-wave 1.1s ease-in-out infinite alternate;
  animation-play-state: paused;
}
.ll-play .ll-wv i { animation-play-state: running; }
@keyframes ll-wave {
  from { transform: scaleY(0.5); }
  to { transform: scaleY(1.6); }
}
.ll-p-time {
  font-size: 0.62rem;
  color: rgba(255, 255, 255, 0.65);
  font-variant-numeric: tabular-nums;
  flex: none;
}
.ll-prog {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  background: #14b8a6;
  width: 0;
  animation: ll-prog 9s linear infinite;
  animation-play-state: paused;
}
.ll-play .ll-prog { animation-play-state: running; }
@keyframes ll-prog {
  0% { width: 0; }
  92%, 100% { width: 100%; }
}
.ll-tline {
  font-size: 0.8rem;
  color: #4b5563;
  padding: 0.42rem 0.2rem;
  border-bottom: 1px dashed #e5e7eb;
  opacity: 0;
  animation-duration: 9s;
  animation-iteration-count: infinite;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  animation-play-state: paused;
}
.ll-tline :deep(b) { color: #111827; }
.ll-tline :deep(.hl) {
  background: linear-gradient(transparent 55%, #ccfbf1 55%);
  border-bottom: 2px solid #14b8a6;
  font-weight: 600;
  color: #111827;
}
.ll-ts {
  font-size: 0.62rem;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
  margin-right: 0.4rem;
}
.ll-tline.ll-tl1 { animation-name: ll-kf-a; }
.ll-tline.ll-tl2 { animation-name: ll-kf-c; }
.ll-tline.ll-tl3 { animation-name: ll-kf-f; }
.ll-play .ll-tline { animation-play-state: running; }
.ll-ia-chips { display: flex; gap: 0.45rem; flex-wrap: wrap; margin-top: 1rem; }
.ll-ia-chip {
  font-size: 0.66rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
  opacity: 0;
  animation: ll-kf-g 9s infinite cubic-bezier(0.16, 1, 0.3, 1);
  animation-play-state: paused;
}
.ll-play .ll-ia-chip { animation-play-state: running; }
.ll-ia-chip:nth-child(2) { animation-name: ll-kf-f; }

/* CRM spotlight */
.ll-kpis {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  margin-bottom: 1.1rem;
}
.ll-kpi {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.8rem;
  padding: 0.7rem 0.85rem;
}
.ll-kpi b {
  display: block;
  font-size: 1.35rem;
  font-weight: 800;
  color: #111827;
  font-variant-numeric: tabular-nums;
}
.ll-kpi span {
  font-size: 0.64rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ll-seg { margin-bottom: 0.65rem; }
.ll-seg-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.72rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 0.3rem;
}
.ll-seg-label b { color: #111827; }
.ll-seg-track {
  height: 0.55rem;
  background: #f3f4f6;
  border-radius: 999px;
  overflow: hidden;
}
.ll-seg-fill {
  height: 100%;
  border-radius: 999px;
  width: 0;
  transition: width 1.1s cubic-bezier(0.25, 1, 0.5, 1);
}
.ll-play .ll-seg-fill { width: var(--w); }
.ll-seg:nth-of-type(2) .ll-seg-fill { transition-delay: 0.15s; }
.ll-seg:nth-of-type(3) .ll-seg-fill { transition-delay: 0.3s; }
.ll-seg:nth-of-type(4) .ll-seg-fill { transition-delay: 0.45s; }
.ll-crm-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.8rem;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
  padding: 0.6rem 0.75rem;
  margin-top: 1rem;
  font-size: 0.76rem;
  color: #4b5563;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.6s cubic-bezier(0.25, 1, 0.5, 1) 0.9s;
}
.ll-play .ll-crm-row { opacity: 1; transform: translateY(0); }
.ll-crm-av {
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #fda4af, #f472b6);
  flex: none;
}
.ll-crm-row b { color: #111827; }

/* Bio spotlight */
.ll-bio-wrap { display: flex; justify-content: center; padding: 0.4rem 0; }
.ll-bphone {
  width: 14rem;
  height: 27rem;
  border-radius: 1.9rem;
  background: linear-gradient(160deg, #1e293b, #0f172a);
  box-shadow: 0 22px 50px -16px rgba(15, 23, 42, 0.4), inset 0 0 0 5px #0f172a;
  position: relative;
  overflow: hidden;
}
.ll-bscreen {
  position: absolute;
  inset: 5px;
  border-radius: 1.6rem;
  background: linear-gradient(180deg, #f0fdfa, #fff 40%);
  overflow: hidden;
  padding: 1.3rem 0.9rem;
  text-align: center;
}
.ll-bio-accent {
  animation: ll-hue 12s infinite;
  animation-play-state: paused;
}
.ll-play .ll-bio-accent { animation-play-state: running; }
@keyframes ll-hue {
  0%, 26% { filter: none; }
  33%, 59% { filter: hue-rotate(150deg); }
  66%, 92% { filter: hue-rotate(255deg); }
  100% { filter: none; }
}
.ll-b-av {
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  margin: 0 auto 0.5rem;
  background: linear-gradient(135deg, #14b8a6, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 1.3rem;
  transform: scale(0);
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.2s;
  box-shadow: 0 8px 18px -6px rgba(13, 148, 136, 0.5);
}
.ll-play .ll-b-av { transform: scale(1); }
.ll-b-name { font-weight: 700; font-size: 0.85rem; color: #111827; }
.ll-b-handle { font-size: 0.64rem; color: #9ca3af; margin-bottom: 0.85rem; }
.ll-b-link {
  display: block;
  background: #0d9488;
  color: #fff;
  font-size: 0.66rem;
  font-weight: 600;
  border-radius: 0.7rem;
  padding: 0.5rem 0.55rem;
  margin-bottom: 0.45rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  box-shadow: 0 6px 14px -6px rgba(13, 148, 136, 0.5);
}
.ll-b-link.ll-ghost {
  background: #fff;
  color: #374151;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
}
.ll-play .ll-b-link { opacity: 1; transform: translateY(0); }
.ll-play .ll-b-link:nth-of-type(1) { transition-delay: 0.45s; }
.ll-play .ll-b-link:nth-of-type(2) { transition-delay: 0.6s; }
.ll-play .ll-b-link:nth-of-type(3) { transition-delay: 0.75s; }
.ll-b-products {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem;
  margin-top: 0.55rem;
}
.ll-b-prod {
  min-width: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.7rem;
  padding: 0.45rem;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1) 0.95s;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
}
.ll-b-prod:nth-child(2) { transition-delay: 1.1s; }
.ll-play .ll-b-prod { opacity: 1; transform: translateY(0); }
.ll-b-ph {
  height: 2.3rem;
  border-radius: 0.45rem;
  background: linear-gradient(135deg, #ccfbf1, #99f6e4);
  margin-bottom: 0.3rem;
}
.ll-b-prod b {
  display: block;
  font-size: 0.58rem;
  color: #111827;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ll-b-prod span {
  display: block;
  font-size: 0.58rem;
  color: #0f766e;
  font-weight: 700;
  text-align: left;
}
.ll-bio-side {
  position: absolute;
  right: -0.4rem;
  top: 2.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.ll-theme-dot {
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.25);
}

/* ---- Final CTA panel ---- */
.ll-cta-panel {
  position: relative;
  overflow: hidden;
  border-radius: 1.6rem;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #0f766e, #0d9488 55%, #0ea5a0);
  box-shadow: 0 30px 70px -24px rgba(13, 148, 136, 0.55);
}
.ll-cta-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(30rem 16rem at 78% -20%, rgba(255, 255, 255, 0.22), transparent 60%),
    radial-gradient(24rem 14rem at 10% 120%, rgba(255, 255, 255, 0.13), transparent 60%);
}

/* ---- Reveal on scroll ---- */
.ll-reveal {
  opacity: 0;
  transform: translateY(26px);
  transition:
    opacity 0.7s cubic-bezier(0.25, 1, 0.5, 1),
    transform 0.7s cubic-bezier(0.25, 1, 0.5, 1);
}
.ll-reveal.ll-visible { opacity: 1; transform: translateY(0); }

@media (prefers-reduced-motion: reduce) {
  .ll-hero-visual *,
  .ll-spot-anim *,
  .ll-reveal,
  .ll-stat-card,
  .ll-step-card {
    animation-duration: 0.001s !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001s !important;
    transition-delay: 0s !important;
  }
  .ll-reveal { opacity: 1; transform: none; }
  .ll-report-row,
  .ll-msg-row,
  .ll-f-el,
  .ll-f-item,
  .ll-f-total,
  .ll-tline,
  .ll-ia-chip,
  .ll-crm-row,
  .ll-b-link,
  .ll-b-prod { opacity: 1 !important; transform: none !important; }
  .ll-bars i { transform: scaleY(1); }
  .ll-seg-fill { width: var(--w); }
  .ll-b-av { transform: scale(1); }
}
</style>
